/**
 * ============================================================================
 *  BILLING CONFIG — every number that decides what Spark charges and what it
 *  costs us lives in this file, and only in this file.
 * ============================================================================
 *
 * Change a price, an allowance or a provider rate here and it spreads to:
 *   - the API's enforcement (core reads PLANS for quotas and persona limits)
 *   - the in-app upgrade page and the public pricing site (both render PLANS)
 *   - cost measurement (the recorder prices every AI and X call from RATES)
 *
 * There are two halves, kept separate on purpose because they answer
 * different questions:
 *
 *   PLANS  — what we CHARGE users, and what their plan ALLOWS them.
 *   RATES  — what providers CHARGE US for the work those users trigger.
 *
 * Margin is the gap between the two. It could not be computed before this
 * file existed, because nothing recorded what anything cost.
 *
 * Rules for editing:
 *   - Numbers only here. Logic reads them; it never re-declares them. Price
 *     tables used to be duplicated across the AI service and every provider,
 *     and had drifted.
 *   - When you change a RATE, update its `source` and `verifiedOn`. A rate with
 *     `verifiedOn: null` is a figure nobody has re-checked, and it says so.
 *   - Where an advertised figure and an enforced one disagree, fix the copy,
 *     not the limit: advertising a ceiling the API will not honour is the
 *     failure this file exists to prevent.
 *
 * This package imports nothing and is bundled into the browser by the app and
 * the marketing site, so keep it plain data.
 */

import {
  type EnforcementPolicy,
  type Plan,
  type Rates,
  SubscriptionTier,
  UNLIMITED,
} from "./types";

// ============================================================================
//  CREDITS — the unit AI usage is metered in
// ============================================================================

/**
 * What one credit is worth, in micro-dollars of actual provider cost.
 *
 * 1 credit = $0.001. Chosen (2026-09-23) as a round, user-legible unit small
 * enough that a typical post costs a two-digit number of credits rather than
 * a fraction of one. Every metered AI action is charged its actual provider
 * cost in credits (`creditsFor`, cost.ts), not a flat per-action weight — so a
 * short refine and a long carousel cost what they actually cost, not the same.
 */
export const CREDIT_MICRO_USD = 1_000;

/**
 * What "about one post" costs, in credits — the figure the UI uses to turn a
 * credit balance back into something people think in ("~40 posts left").
 *
 * Measured 2026-09-22: a 30-article evaluation run against gemini-3.6-flash
 * (packages/eval) averaged $0.0100 (10,000 micro-USD) per generated post, which
 * is exactly 10 credits at the $0.001 rate above. Re-measure and update this
 * — and the plan allowances below, which were sized from it — after a model
 * change moves the real cost per post.
 */
export const STANDARD_POST_CREDITS = 10;

// ============================================================================
//  PLANS — what we charge, and what each plan allows
// ============================================================================

export const PLANS: readonly Plan[] = [
  {
    key: SubscriptionTier.FREE,
    name: "Free",
    monthlyPrice: 0,
    annualPrice: 0,
    tagline: "Perfect for trying out Spark.",
    cta: "Current Plan",
    // Previously advertised "LinkedIn only" while the API let Free post to X
    // without limit — the most expensive thing the product does, uncapped, on
    // the plan that pays nothing. Now a small allowance, and the copy says so.
    // Credits: the old 10 generations/month x STANDARD_POST_CREDITS.
    limits: {
      monthlyCredits: 100,
      monthlyGenerations: 10,
      personas: 2,
      monthlyXPosts: 5,
    },
    extraFeatures: [
      { label: "Community support", included: true },
      { label: "Analytics", included: false },
      { label: "Scheduling & calendar", included: true },
    ],
  },
  {
    key: SubscriptionTier.PRO,
    name: "Pro",
    monthlyPrice: 12,
    annualPrice: 10,
    tagline: "Everything you need to grow your presence.",
    badge: "Most Popular",
    highlighted: true,
    cta: "Upgrade to Pro",
    // Was advertised as "Unlimited personas" in-app and "10" on the website
    // while the API enforced 50. 50 is what the code does.
    // X posts sized so that even if every post has a link, X costs about
    // half the plan price. Credits: the old 100 generations/month x
    // STANDARD_POST_CREDITS.
    limits: {
      monthlyCredits: 1_000,
      monthlyGenerations: 100,
      personas: 50,
      monthlyXPosts: 30,
    },
    extraFeatures: [
      { label: "LinkedIn and X", included: true },
      { label: "Priority support", included: true },
      { label: "Basic analytics (coming soon)", included: true },
      { label: "Scheduling & calendar", included: true },
    ],
  },
  {
    key: SubscriptionTier.TEAM,
    name: "Team",
    monthlyPrice: 39,
    annualPrice: 32,
    tagline: "Collaborate with your entire team.",
    cta: "Upgrade to Team",
    // Advertised "Unlimited" personas; the API enforces 100. Credits: the old
    // 500 generations/month x STANDARD_POST_CREDITS.
    limits: {
      monthlyCredits: 5_000,
      monthlyGenerations: 500,
      personas: 100,
      monthlyXPosts: 95,
    },
    extraFeatures: [
      { label: "Everything in Pro", included: true },
      { label: "1 Room, 5 members (coming soon)", included: true },
      { label: "Advanced analytics (coming soon)", included: true },
      { label: "Team admin tools (coming soon)", included: true },
    ],
  },
  {
    key: SubscriptionTier.ENTERPRISE,
    name: "Enterprise",
    monthlyPrice: null,
    annualPrice: null,
    tagline: "Dedicated support & infrastructure at scale.",
    cta: "Contact Sales",
    // Credits genuinely are uncapped. Personas are not: the API enforces
    // 200, so "Unlimited rooms & members" stays but a persona claim would not.
    limits: {
      monthlyCredits: UNLIMITED,
      monthlyGenerations: UNLIMITED,
      personas: 200,
      monthlyXPosts: UNLIMITED,
    },
    extraFeatures: [
      { label: "Everything in Team", included: true },
      { label: "Unlimited rooms & members (coming soon)", included: true },
      { label: "Custom SLA", included: true },
      { label: "Dedicated success manager", included: true },
      { label: "24 / 7 phone support", included: true },
    ],
  },
] as const;

// ============================================================================
//  CUSTOM PRICES — a per-account monthly price set by an admin
// ============================================================================

/**
 * The most an admin may set as one account's monthly price, in USD.
 *
 * A custom price replaces the list price for one account: a discount, or $0
 * for a trial. The cap is there to catch a price typed in cents or micro-
 * dollars by mistake, not to bound what Enterprise could be charged; raise it
 * if a real contract exceeds it.
 */
export const CUSTOM_PRICE_CAP_USD = 10_000;

// ============================================================================
//  ENFORCEMENT — what happens at a limit
// ============================================================================

/**
 * Graduated, not a wall.
 *
 * People exceed their limits routinely, so hitting one is not an error and
 * does not page anyone. Past the limit a user gets a grace allowance to finish
 * what they are doing — the pricing FAQ promised this long before the code did
 * it — during which interactive requests are spaced out. Past the allowance,
 * requests are refused with the reset date.
 */
export const ENFORCEMENT: EnforcementPolicy = {
  grace: {
    ratio: 0.1,
    minimumGenerations: 2,
    // Two standard posts' worth of credits — the same floor as before,
    // re-denominated: STANDARD_POST_CREDITS * 2.
    minimumCredits: 20,
    minimumXPosts: 1,
  },
  throttle: {
    minIntervalSeconds: 60,
  },
};

// ============================================================================
//  RATES — what providers charge us
// ============================================================================

export const RATES: Rates = {
  ai: {
    models: {
      /**
       * The primary model: every generation, refine, variation and carousel,
       * and (since its predecessor was shut down) source extraction.
       *
       * The previous figures here were $0.15 / $0.60 — preview-era prices —
       * and cost was computed from visible output only, dropping thinking
       * tokens that Google bills at the output rate. Together that recorded a
       * typical post at about a fifteenth of its real cost.
       */
      "gemini-2.5-flash": {
        provider: "gemini",
        inputUsdPerMillion: 0.3,
        outputUsdPerMillion: 2.5,
        thinkingBilledAsOutput: true,
        source: "https://ai.google.dev/gemini-api/docs/pricing",
        verifiedOn: "2026-09-14",
      },

      /**
       * Gemini 3 Flash — Google's named replacement for 2.5 Flash, and the
       * target of the staged migration. Not the default yet: these prices are
       * from the Gemini API and Vertex availability in our region still has to
       * be confirmed on staging before production follows.
       *
       * The listed prices are promotional and double on 1 January 2027.
       */
      "gemini-3.6-flash": {
        provider: "gemini",
        inputUsdPerMillion: 0.75,
        outputUsdPerMillion: 3.75,
        thinkingBilledAsOutput: true,
        scheduledChange: {
          from: "2027-01-01",
          inputUsdPerMillion: 1.5,
          outputUsdPerMillion: 7.5,
        },
        source: "https://ai.google.dev/gemini-api/docs/pricing",
        verifiedOn: "2026-09-14",
      },

      /**
       * Gemini 3 Flash-Lite — cheaper per token than the 2.5 Flash in use
       * today, at lower quality. Priced here so a quality-versus-cost
       * comparison can be made from measured figures rather than guessed.
       */
      "gemini-3.1-flash-lite": {
        provider: "gemini",
        inputUsdPerMillion: 0.25,
        outputUsdPerMillion: 1.5,
        thinkingBilledAsOutput: true,
        source: "https://ai.google.dev/gemini-api/docs/pricing",
        verifiedOn: "2026-09-14",
      },

      // Fallbacks. The router only reaches these when their API keys are
      // configured and Gemini has failed — which is when the ledger is least
      // able to afford a guess, so these are verified like the primary.
      "gpt-4o-mini": {
        provider: "openai",
        inputUsdPerMillion: 0.15,
        outputUsdPerMillion: 0.6,
        thinkingBilledAsOutput: false,
        source: "https://developers.openai.com/api/docs/pricing",
        verifiedOn: "2026-09-21",
      },
      /**
       * Replaces claude-3-5-sonnet-20241022, which Anthropic retired on
       * 28 October 2025: every fallback to it failed, priced at $3 / $15
       * from a figure nobody had checked.
       */
      "claude-haiku-4-5-20251001": {
        provider: "anthropic",
        inputUsdPerMillion: 1,
        outputUsdPerMillion: 5,
        thinkingBilledAsOutput: false,
        source: "https://platform.claude.com/docs/en/about-claude/pricing",
        verifiedOn: "2026-09-21",
      },
      "claude-sonnet-5": {
        provider: "anthropic",
        inputUsdPerMillion: 2,
        outputUsdPerMillion: 10,
        thinkingBilledAsOutput: false,
        source: "https://platform.claude.com/docs/en/about-claude/pricing",
        verifiedOn: "2026-09-21",
      },

      /**
       * OpenAI's open-weight models, served on Vertex AI Model-as-a-Service.
       * Not a fallback for product traffic — see `vertex-maas.ts` — used
       * only as the generation eval harness's independent voice grader,
       * where a Gemini model grading Gemini output would not be one.
       * Reasoning tokens are reported as a subset of `completion_tokens`
       * (the OpenAI convention), already inside `completionTokens`, so
       * `thinkingBilledAsOutput: false` here is what stops them being
       * counted twice (contrast Gemini, which reports them separately and
       * bills them additionally).
       */
      "openai/gpt-oss-120b-maas": {
        provider: "vertex-maas",
        inputUsdPerMillion: 0.09,
        outputUsdPerMillion: 0.36,
        thinkingBilledAsOutput: false,
        source: "https://cloud.google.com/vertex-ai/generative-ai/pricing",
        verifiedOn: "2026-09-23",
      },
      "openai/gpt-oss-20b-maas": {
        provider: "vertex-maas",
        inputUsdPerMillion: 0.07,
        outputUsdPerMillion: 0.25,
        thinkingBilledAsOutput: false,
        source: "https://cloud.google.com/vertex-ai/generative-ai/pricing",
        verifiedOn: "2026-09-23",
      },
    },
  },

  /**
   * X API, pay-per-use (X's default billing since February 2026).
   *
   * X is the most expensive thing Spark does per operation. A post containing
   * a URL costs roughly thirteen times a plain post, and around sixty times the
   * AI call that wrote it — and repurposed content often links its source.
   *
   * OAuth token exchange and refresh are not listed as billable, so the adapter
   * does not record them. Delete and media upload are not listed either; they
   * are recorded as unpriced rather than assumed free.
   *
   * LinkedIn publishing has no per-call fee, so it has no rates here.
   */
  x: {
    billing: "pay-per-use",
    source: "https://docs.x.com/x-api/getting-started/pricing",
    verifiedOn: "2026-09-16",
    postCreateUsd: 0.015,
    postCreateWithUrlUsd: 0.2,
    userReadUsd: 0.01,
    // "Post Read: $0.005 per resource". X prices some user-specific reads at
    // $0.001 ("owned reads": /2/users/{id}/tweets and similar), but reading a
    // post by id is not one of those, so this is the rate that applies to us.
    postReadUsd: 0.005,
    // X publishes no charge for deleting a post, and its webhook documentation
    // lists post.delete as not billed. Zero, not null: we know this one is
    // free, rather than not knowing what it costs.
    postDeleteUsd: 0,
    // Still no published price for media upload.
    mediaUploadUsd: null,
  },
};
